import type { ClarifyType } from '@/lib/chat/clarify';
import type { ClientPreferences, PreferenceProvenanceEntry } from '@/types/preferences';

export type AgentKnowledgePreference = {
  field: string;
  label: string;
  value: string;
  source?: string;
  confidence?: number;
  reviewGated?: boolean;
};

export type AgentKnowledgeClarification = {
  type: ClarifyType;
  label: string;
  text: string;
  placeholderCount: number;
  skipped?: boolean;
  updatedAt?: number;
};

export type AgentKnowledgeMemory = {
  id: string;
  label: string;
  source?: string;
  confidence?: number;
  url?: string;
};

export type AgentKnowledgeDisplay = {
  clientLabel: string;
  preferences: AgentKnowledgePreference[];
  clarifications: AgentKnowledgeClarification[];
  memories: AgentKnowledgeMemory[];
  hasKnowledge: boolean;
};

type StoredClarificationEntry = {
  text?: string;
  placeholderCount?: number;
  updatedAt?: number;
  skipped?: boolean;
};

type MessageLike = {
  meta?: unknown;
};

type BuildAgentKnowledgeDisplayInput = {
  clientName?: string | null;
  preferences?: ClientPreferences | null;
  provenance?: Record<string, PreferenceProvenanceEntry | undefined>;
  storedClarifications?: Partial<Record<ClarifyType, StoredClarificationEntry>> | null;
  messages?: MessageLike[] | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function formatSource(source?: string): string | undefined {
  if (!source) return undefined;
  if (source === 'session_override') return 'session';
  if (source === 'user_client_explicit') return 'explicit';
  if (source === 'user_client_inferred') return 'inferred';
  if (source === 'client_baseline') return 'client baseline';
  if (source === 'stylebook_default') return 'stylebook';
  return source.replace(/_/g, ' ');
}

function formatClarificationType(type: ClarifyType): string {
  switch (type) {
    case 'press':
      return 'Press release';
    case 'pitch':
      return 'Pitch';
    case 'social':
      return 'Social';
    case 'research':
      return 'Research';
    case 'quality_review':
      return 'Quality review';
    default:
      return 'General';
  }
}

function pushPreference(
  target: AgentKnowledgePreference[],
  provenance: Record<string, PreferenceProvenanceEntry | undefined> | undefined,
  field: string,
  label: string,
  value: unknown,
  reviewGated = false,
): void {
  const text = Array.isArray(value)
    ? value.map((entry) => normalizeString(entry)).filter(Boolean).join(', ')
    : normalizeString(value);
  if (!text) return;
  const source = provenance?.[field]?.source;
  const confidence = provenance?.[field]?.confidence;
  target.push({
    field,
    label,
    value: text,
    ...(formatSource(source) ? { source: formatSource(source) } : {}),
    ...(typeof confidence === 'number' ? { confidence } : {}),
    ...(reviewGated ? { reviewGated: true } : {}),
  });
}

function collectPreferences(
  preferences: ClientPreferences | null | undefined,
  provenance: Record<string, PreferenceProvenanceEntry | undefined> | undefined,
): AgentKnowledgePreference[] {
  if (!preferences) return [];
  const items: AgentKnowledgePreference[] = [];
  pushPreference(items, provenance, 'locale', 'Locale', preferences.locale?.toUpperCase());
  pushPreference(items, provenance, 'tone', 'Tone', preferences.tone);
  pushPreference(items, provenance, 'spokesperson', 'Spokesperson', preferences.spokesperson?.name);
  pushPreference(items, provenance, 'mediaContact', 'Media contact', preferences.mediaContact?.email || preferences.mediaContact?.name);
  pushPreference(items, provenance, 'lengthPreference', 'Length', preferences.lengthPreference);
  pushPreference(items, provenance, 'structurePreference', 'Structure', preferences.structurePreference);
  pushPreference(items, provenance, 'styleNotes', 'Style notes', preferences.styleNotes);
  pushPreference(items, provenance, 'requiredTerms', 'Required terms', preferences.requiredTerms, true);
  pushPreference(items, provenance, 'forbiddenTerms', 'Forbidden terms', preferences.forbiddenTerms, true);
  return items;
}

function collectClarifications(
  storedClarifications: Partial<Record<ClarifyType, StoredClarificationEntry>> | null | undefined,
): AgentKnowledgeClarification[] {
  if (!storedClarifications) return [];
  return Object.entries(storedClarifications)
    .map(([type, entry]) => {
      const typedType = type as ClarifyType;
      const text = normalizeString(entry?.text);
      if (!text && !entry?.skipped) return null;
      return {
        type: typedType,
        label: formatClarificationType(typedType),
        text: text || 'Skipped by user',
        placeholderCount: typeof entry?.placeholderCount === 'number' ? entry.placeholderCount : 0,
        ...(entry?.skipped ? { skipped: true } : {}),
        ...(typeof entry?.updatedAt === 'number' ? { updatedAt: entry.updatedAt } : {}),
      };
    })
    .filter((entry): entry is AgentKnowledgeClarification => !!entry)
    .sort((left, right) => (right.updatedAt || 0) - (left.updatedAt || 0));
}

function memoryFromRaw(raw: unknown, fallbackId: string): AgentKnowledgeMemory | null {
  if (typeof raw === 'string') {
    const id = raw.trim();
    return id ? { id, label: id } : null;
  }
  const record = asRecord(raw);
  if (!record) return null;
  const id = normalizeString(record.id) || normalizeString(record.path) || fallbackId;
  const label = normalizeString(record.title)
    || normalizeString(record.text)
    || normalizeString(record.url)
    || id;
  if (!label) return null;
  return {
    id,
    label,
    ...(formatSource(normalizeString(record.source)) ? { source: formatSource(normalizeString(record.source)) } : {}),
    ...(typeof record.confidence === 'number' ? { confidence: record.confidence } : {}),
    ...(normalizeString(record.url) ? { url: normalizeString(record.url) } : {}),
  };
}

function collectMemories(messages: MessageLike[] | null | undefined): AgentKnowledgeMemory[] {
  const memories: AgentKnowledgeMemory[] = [];
  const seen = new Set<string>();
  for (const message of messages || []) {
    const meta = asRecord(message.meta);
    const provenance = asRecord(meta?.provenance);
    const rawMemories = [
      ...(Array.isArray(meta?.memories) ? meta.memories : []),
      ...(Array.isArray(provenance?.memoriesUsed) ? provenance.memoriesUsed : []),
    ];
    rawMemories.forEach((raw, index) => {
      const memory = memoryFromRaw(raw, `memory-${memories.length + index + 1}`);
      if (!memory) return;
      if (seen.has(memory.id)) return;
      seen.add(memory.id);
      memories.push(memory);
    });
  }
  return memories.slice(-12).reverse();
}

export function buildAgentKnowledgeDisplay(input: BuildAgentKnowledgeDisplayInput): AgentKnowledgeDisplay {
  const preferences = collectPreferences(input.preferences, input.provenance);
  const clarifications = collectClarifications(input.storedClarifications);
  const memories = collectMemories(input.messages);
  return {
    clientLabel: input.clientName || 'General mode',
    preferences,
    clarifications,
    memories,
    hasKnowledge: Boolean(preferences.length || clarifications.length || memories.length),
  };
}
